import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Card } from '@mui/material';

export const HubContainer = styled(Box)({
  height: '100vh', // Usar altura fixa de 100vh
  maxHeight: '100vh', // Limitar a altura máxima para evitar overflow
  backgroundColor: '#0d1828',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden', // Impedir overflow que causaria scroll externo
});

export const Header = styled(Box)({
  padding: '32px 40px',
  backgroundColor: '#162238',
  color: 'white',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
});

export const HeaderContent = styled(Box)({
  maxWidth: '1400px', // Aumentado para corresponder ao novo MainContent
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const Title = styled(Typography)({
  fontWeight: 'bold',
  fontSize: '28px',
  color: 'white',
});

export const Subtitle = styled(Typography)({
  color: 'rgba(255, 255, 255, 0.7)',
  marginTop: '8px',
});

export const CreateButton = styled(Button)({
  backgroundColor: '#2196f3',
  color: 'white',
  paddingLeft: '16px',
  paddingRight: '16px',
  textTransform: 'none',
  fontWeight: 'bold',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#1976d2',
  },
});

export const MainContent = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto',
  padding: '24px 40px 0', // Padding superior reduzido e sem padding inferior
  flexGrow: 1,
  overflow: 'hidden', // Impedir overflow externo
  display: 'flex',
  flexDirection: 'column',
});

export const SectionTitle = styled(Typography)({
  fontSize: '22px',
  fontWeight: 600,
  color: 'white',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between', // Permite posicionar o indicador de rolagem à direita
  '& svg': {
    marginRight: '12px',
    color: '#2196f3',
  },
});

export const ProjectsGrid = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasScroll',
})<{ hasScroll?: boolean }>(({
  theme,
  hasScroll,
}) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(380px, 1fr))', // 2 colunas lado a lado
  gridAutoRows: 'min-content', // Permitir que as linhas se ajustem ao conteúdo
  gap: '24px 24px', // Espaçamento consistente entre os cards (vertical e horizontal)
  marginBottom: '20px', // Reduzido para caber na viewport
  position: 'relative',
  
  // Altura fixa para caber na viewport
  height: 'calc(100vh - 260px)',
  maxHeight: 'calc(100vh - 260px)',
  overflowY: 'auto', // Apenas scroll vertical na grade
  overflowX: 'hidden',
  padding: '7px 16px 0 0', // Padding somente à direita para acomodar a barra de rolagem, sem padding superior ou inferior
  
  // Estilo personalizado do scrollbar para esta grade
  '&::-webkit-scrollbar': {
    width: '8px',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
    margin: '0', // Removido margin para alinhar perfeitamente o scroll
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(33, 150, 243, 0.3)',
    borderRadius: '8px',
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(33, 150, 243, 0.5)',
    borderRadius: '8px',
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
  },
  
  // Regras para diferentes tamanhos de tela
  '@media (max-width: 1200px)': {
    gridTemplateColumns: 'repeat(2, 1fr)', // 2 colunas em tela média
    height: 'calc(100vh - 240px)',
    maxHeight: 'calc(100vh - 240px)',
  },
  '@media (max-width: 960px)': {
    gridTemplateColumns: '1fr', // 1 coluna em telas menores
    height: 'calc(100vh - 220px)',
    maxHeight: 'calc(100vh - 220px)',
  },
  '@media (max-width: 600px)': {
    height: 'calc(100vh - 200px)',
    maxHeight: 'calc(100vh - 200px)',
    gap: '16px', // Menor gap em telas muito pequenas
  },
  // Ajustes para telas com diferentes alturas
  '@media (max-height: 700px)': {
    height: 'calc(100vh - 200px)',
    maxHeight: 'calc(100vh - 200px)',
  },
  '@media (max-height: 600px)': {
    height: 'calc(100vh - 180px)',
    maxHeight: 'calc(100vh - 180px)',
  }
}));

export const EmptyState = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px',
  backgroundColor: '#1E2E46',
  borderRadius: '12px',
  textAlign: 'center',
  marginTop: '32px',
});

export const EmptyStateIcon = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '80px',
  height: '80px',
  backgroundColor: 'rgba(33, 150, 243, 0.1)',
  borderRadius: '50%',
  marginBottom: '24px',
  '& svg': {
    fontSize: '40px',
    color: '#2196f3',
  },
});

export const EmptyStateText = styled(Typography)({
  color: 'white',
  fontWeight: 'bold',
  marginBottom: '8px',
});

export const EmptyStateSubtext = styled(Typography)({
  color: 'rgba(255, 255, 255, 0.7)',
  marginBottom: '24px',
  maxWidth: '400px',
});

export const SearchContainer = styled(Box)({
  marginBottom: '16px',
  display: 'flex',
  gap: '16px',
});

export const TabsContainer = styled(Box)({
  marginBottom: '16px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
});

export const TabButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active }) => ({
  color: active ? 'white' : 'rgba(255, 255, 255, 0.6)',
  borderBottom: active ? '2px solid #2196f3' : 'none',
  borderRadius: 0,
  paddingBottom: '12px',
  paddingTop: '12px',
  marginRight: '24px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: active ? 600 : 400,
  '&:hover': {
    backgroundColor: 'transparent',
    color: 'white',
  },
}));

export const ProjectsCountText = styled(Typography)({
  color: 'rgba(255, 255, 255, 0.6)',
  fontSize: '14px',
  marginBottom: '16px',
});
